import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { Children, useState, useRef, isValidElement } from 'react';
import classNames from 'classnames';
import { safeInvoke, EventEmitter } from '../utils';
import { CLS_PREFIX } from '../../constants';
import ComboBoxDropdown, { findNextSafeIndex } from '../ComboBoxDropdown';
import Translate from '../Translate';
import SelectControl from './SelectControl';
import SelectOptionGroup from './SelectOptionGroup';
import SelectOption from './SelectOption';

// eslint-disable-next-line default-param-last
var collect = function collect(children) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var group = arguments.length > 2 ? arguments[2] : undefined;
  var groups = [];
  Children.forEach(children, function (child) {
    if (isValidElement(child) && (child.type === SelectOptionGroup || child.type === 'optgroup')) {
      var _child$props = child.props,
          label = _child$props.label,
          _children = _child$props.children,
          props = _objectWithoutProperties(_child$props, ["label", "children"]);

      var _group = {
        label: String(label),
        options: [],
        props: props
      };
      groups.push(_group);
      collect(_children, options, _group);
    } else if (isValidElement(child) && (child.type === SelectOption || child.type === 'option')) {
      // eslint-disable-next-line prefer-const
      var _child$props2 = child.props,
          _value = _child$props2.value,
          propLabel = _child$props2.label,
          disabled = _child$props2.disabled,
          _children2 = _child$props2.children,
          _props = _objectWithoutProperties(_child$props2, ["value", "label", "disabled", "children"]);

      if (typeof _value !== 'string' && typeof _value !== 'number' && typeof _children2 === 'string') {
        _value = _children2;
      }

      if (!_value) {
        throw new Error('The value can only be a number or a string.');
      }

      var _label;

      if (propLabel && typeof propLabel === 'string') {
        _label = child.props.label;
      } else if (typeof child.props.children === 'string') {
        _label = child.props.children;
      }

      if (!_label) {
        throw new Error('The label can only be a string.');
      }

      var _option = {
        value: _value,
        label: _label,
        children: _children2,
        disabled: Boolean(disabled),
        group: group,
        props: _props
      };
      options.push(_option);

      if (group) {
        group.options.push(_option);
      }
    }
  });

  if (groups.length === 0) {
    groups.push({
      options: options
    });
  }

  return {
    groups: groups,
    options: options
  };
};

var getNextValue = function getNextValue(value, prevValue) {
  if (Array.isArray(prevValue)) {
    return [].concat(_toConsumableArray(prevValue), [value]);
  }

  return value;
};

/**
 * `Select` component is used for making a choice among a set of related options.
 * @since 2.3.0
 */
var Select = function Select(_ref) {
  var name = _ref.name,
      size = _ref.size,
      _ref$baseClassName = _ref.baseClassName,
      baseClassName = _ref$baseClassName === void 0 ? "".concat(CLS_PREFIX, "select") : _ref$baseClassName,
      className = _ref.className,
      children = _ref.children,
      defaultValue = _ref.defaultValue,
      propValue = _ref.value,
      onChange = _ref.onChange,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$searchable = _ref.searchable,
      searchable = _ref$searchable === void 0 ? false : _ref$searchable,
      _ref$clearable = _ref.clearable,
      clearable = _ref$clearable === void 0 ? false : _ref$clearable,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      placeholder = _ref.placeholder,
      props = _objectWithoutProperties(_ref, ["name", "size", "baseClassName", "className", "children", "defaultValue", "value", "onChange", "disabled", "searchable", "clearable", "multiple", "placeholder"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      opened = _useState2[0],
      setOpened = _useState2[1];

  var controlRef = useRef(null);
  var emitterRef = useRef(new EventEmitter());

  var _useState3 = useState(function () {
    if (typeof defaultValue === 'undefined') {
      return multiple ? [] : undefined;
    }

    return defaultValue;
  }),
      _useState4 = _slicedToArray(_useState3, 2),
      value = _useState4[0],
      setValue = _useState4[1];

  if (typeof propValue !== 'undefined') {
    value = propValue;

    setValue = function setValue() {};
  }

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      filterValue = _useState6[0],
      setFilterValue = _useState6[1];

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      highlightedIndex = _useState8[0],
      setHighlightedIndex = _useState8[1];

  var _collect = collect(children),
      groups = _collect.groups,
      options = _collect.options;

  var current;

  if (Array.isArray(value)) {
    var _current = [];
    value.forEach(function (v) {
      var option = options.find(function (o) {
        return o.value === v;
      });

      if (option) {
        _current.push(option);
      }
    });
    current = _current;
  } else {
    var _current2 = options.find(function (o) {
      return o.value === value;
    });

    current = _current2;
  }

  var groupFns = [];
  var optionFns = []; // eslint-disable-next-line func-style

  function createInclude(fns) {
    return function (item) {
      return fns.every(function (fn) {
        return fn(item);
      });
    };
  }

  if (Array.isArray(value) && value.length > 0) {
    var v = value;
    groupFns.push(function () {
      return false;
    });
    optionFns.push(function (option) {
      return v.indexOf(option.value) === -1;
    });
  }

  if (filterValue) {
    var lowerFilterValue = filterValue.toLowerCase();
    groupFns.push(function (group) {
      return !!group.label && group.label.toLowerCase().indexOf(lowerFilterValue) === 0;
    });
    optionFns.push(function (option) {
      return option.label.toLowerCase().indexOf(lowerFilterValue) === 0;
    });
  }

  if (groupFns.length || optionFns.length) {
    var includeGroup = createInclude(groupFns);
    var includeOption = createInclude(optionFns);
    options = [];
    groups = groups.filter(function (group) {
      if (includeGroup(group)) {
        options = [].concat(_toConsumableArray(options), _toConsumableArray(group.options));
        return true;
      }

      group.options = group.options.filter(function (option) {
        if (includeOption(option)) {
          options.push(option);
          return true;
        }

        return false;
      });
      return group.options.length > 0;
    });
  }

  if (highlightedIndex && highlightedIndex > options.length - 1) {
    setHighlightedIndex(0);
  }

  var handleFilterChange = function handleFilterChange(nextFilterValue) {
    handleOpen();
    setFilterValue(nextFilterValue);
  };

  var focusHighlightedIndex = function focusHighlightedIndex(index) {
    emitterRef.current.emit('focus', index);
  };

  var handleHighlightChange = function handleHighlightChange(diff) {
    if (!opened) {
      handleOpen();
      return;
    }

    var nextIndex = findNextSafeIndex(highlightedIndex, diff, options);

    if (nextIndex !== -1) {
      setHighlightedIndex(nextIndex);
      focusHighlightedIndex(nextIndex);
    }
  };

  var handleHighlightConfirm = function handleHighlightConfirm(index) {
    if (!opened && index === undefined) {
      return;
    }

    var option = options[index === undefined ? highlightedIndex : index];

    if (!option) {
      return;
    }

    if (option.disabled) {
      return;
    }

    var nextValue = getNextValue(option.value, value);
    setValue(nextValue);
    setFilterValue('');

    if (!multiple) {
      handleClose();
    }

    safeInvoke(onChange, nextValue);
  };

  var handleHighlightSearch = function handleHighlightSearch(query) {
    var lowerQuery = query.toLowerCase();
    var index = options.findIndex(function (o) {
      return !o.disabled && o.label.toLowerCase().indexOf(lowerQuery) === 0;
    });

    if (index !== -1) {
      setHighlightedIndex(index);
      focusHighlightedIndex(index);

      if (!opened) {
        handleHighlightConfirm(index);
      }
    }
  };

  var handleOpen = function handleOpen() {
    setOpened(true);
  };

  var handleClose = function handleClose() {
    setOpened(false);
    setHighlightedIndex(0);
  };

  var handleSelect = function handleSelect(optionValue) {
    var nextValue = getNextValue(optionValue, value);
    setValue(nextValue);
    setFilterValue('');

    if (!multiple) {
      handleClose();
    }

    safeInvoke(onChange, nextValue);
  };

  var handleHighlightedIndexChange = function handleHighlightedIndexChange(index) {
    setHighlightedIndex(index);
  };

  var handleValueRemove = function handleValueRemove(valueToRemove) {
    if (Array.isArray(value)) {
      var nextValue;

      if (valueToRemove === undefined) {
        nextValue = _toConsumableArray(value);
        nextValue.pop();
      } else {
        nextValue = value.filter(function (v) {
          return v !== valueToRemove;
        });
      }

      setValue(nextValue);
      safeInvoke(onChange, nextValue);
    } else {
      setValue(undefined);
      safeInvoke(onChange, undefined);
    }
  };

  var handleClear = function handleClear() {
    var nextValue = Array.isArray(value) ? [] : undefined;
    setFilterValue('');
    setValue(nextValue);
    safeInvoke(onChange, nextValue);
  };

  var control = React.createElement(SelectControl, {
    name: name,
    current: current,
    filterValue: filterValue,
    searchable: searchable,
    clearable: clearable,
    multiple: multiple,
    disabled: disabled,
    onFilterChange: handleFilterChange,
    onHighlightChange: handleHighlightChange,
    onHighlightConfirm: handleHighlightConfirm,
    onHighlightSearch: handleHighlightSearch,
    onOpen: handleOpen,
    onClose: handleClose,
    onClear: handleClear,
    onValueRemove: handleValueRemove,
    placeholder: placeholder,
    ref: controlRef
  });
  return React.createElement(ComboBoxDropdown, _extends({
    className: classNames(baseClassName, className, _defineProperty({}, "".concat(baseClassName, "--size-").concat(size), size)),
    control: control,
    controlRef: controlRef,
    opened: opened && !disabled,
    disabled: disabled,
    groups: groups,
    options: options,
    onSelect: handleSelect,
    highlightedIndex: highlightedIndex,
    onHighlightedIndexChange: handleHighlightedIndexChange,
    onClose: handleClose,
    noOptions: React.createElement(Translate, {
      content: "Select.noOptions",
      fallback: "No options"
    }),
    emitter: emitterRef.current,
    value: value
  }, props));
};

export default Select;